import { MAX_UPLOAD_BYTES } from '@batiflow/contracts';
import { isolate } from '@batiflow/ui';
import { Button, ProgressBar, __css, tokens } from '@fluentui/react-components';
import {
  ArrowClockwise16Regular,
  CheckmarkCircle16Filled,
  Document16Regular,
  ErrorCircle16Filled,
  Prohibited16Regular,
  Send16Regular,
  ShieldTask16Regular,
} from '@fluentui/react-icons';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocale } from '@/app/providers/i18n-provider';
import type { UploadController } from './upload-controller';
import {
  formatBytes,
  formatPercent,
  type UploadIssue,
  type UploadPhase,
  type UploadRow,
} from './upload-model';

const useStyles = __css({"list":{"dclx09":"ftrb29c","Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","B1hrpyx":"f1jat206","mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1wn3y0l"},"row":{"mc9l5x":"f22iagw","Eh141a":"fni485r","Bt984gj":"f122n59","Brf1p80":"f1869bpl","i8kkvl":"f1xcgk68","Belr9w4":"f778t4r","Dbcxam":0,"rjzwhg":0,"Bblux5w":"f9b8bpc","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"f12n3xlw","De3pzq":"fxugw4r","Beyfa6y":0,"Bbmb7ep":0,"Btl43ni":0,"B7oj6ja":0,"Dimara":"ft85np5"},"info":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1j0q4x9","Bh6795r":"fqerorx","xawz":"fb2jqpd","Bgto7am":"fuzgk9b"},"head":{"mc9l5x":"f22iagw","Bt984gj":"f122n59","i8kkvl":"fsnqrgy"},"name":{"Btd35i7":"fjksvth","Bhrd7zp":"fl43uef"},"size":{"sj55zd":"f11d4kpn","Be2twd7":"fy9rknc"},"status":{"mc9l5x":"f22iagw","Bt984gj":"f6jr5hl","i8kkvl":"f1ufnopg","Be2twd7":"fy9rknc","Bg96gwp":"fwrc4pm"},"statusIcon":{"Bnnss6s":"fi64zpg","Cgygva":"f7j5a1j"},"ok":{"sj55zd":"ffmvakt"},"bad":{"sj55zd":"f1hcrxcs"},"neutral":{"sj55zd":"fkfq4zb"},"message":{"Btd35i7":"fjksvth"},"actions":{"mc9l5x":"f22iagw","i8kkvl":"f1ufnopg"}});

const STATUS_ICON: Record<UploadPhase, ReactElement> = {
  sending: <Send16Regular />,
  scanning: <ShieldTask16Regular />,
  added: <CheckmarkCircle16Filled />,
  refused: <Prohibited16Regular />,
  failed: <ErrorCircle16Filled />,
};

/** The plain-language sentence of an issue (`upload.issue.*`). */
export function useIssueMessage(): (issue: UploadIssue) => string {
  const { t } = useTranslation('documents');
  const locale = useLocale();
  return (issue) =>
    issue.code === 'type' && !issue.extension
      ? t('upload.issue.typeNoExtension')
      : t(`upload.issue.${issue.code}`, {
          extension: issue.extension ? isolate(`.${issue.extension}`) : '',
          max: isolate(formatBytes(MAX_UPLOAD_BYTES, locale)),
        });
}

export interface UploadRowsProps {
  rows: readonly UploadRow[];
  controller: UploadController;
}

/** One line per file: name, size, progress, the state as text and icon, and its actions. */
export function UploadRows({ rows, controller }: UploadRowsProps) {
  const { t } = useTranslation('documents');
  const styles = useStyles();
  const locale = useLocale();
  const issueMessage = useIssueMessage();
  if (rows.length === 0) return null;

  return (
    <ul className={styles.list} aria-label={t('upload.listLabel')}>
      {rows.map((row) => {
        const name = isolate(row.name);
        const label = t(`upload.status.${row.phase}`);
        const text = row.issue
          ? t('upload.withIssue', { status: label, issue: issueMessage(row.issue) })
          : label;
        const tone =
          row.phase === 'added'
            ? styles.ok
            : row.phase === 'refused' || row.phase === 'failed'
              ? styles.bad
              : styles.neutral;
        return (
          <li key={row.id} className={styles.row}>
            <div className={styles.info}>
              <div className={styles.head}>
                <Document16Regular aria-hidden="true" />
                <bdi className={styles.name}>{row.name}</bdi>
                <bdi dir="ltr" className={styles.size}>
                  {formatBytes(row.size, locale)}
                </bdi>
              </div>
              {row.phase === 'sending' ? (
                <ProgressBar
                  value={row.percent / 100}
                  aria-label={t('upload.progressLabel', { name })}
                />
              ) : row.phase === 'scanning' ? (
                <ProgressBar aria-label={t('upload.scanningLabel', { name })} />
              ) : null}
              <div className={`${styles.status} ${tone}`} role="status">
                <span className={styles.statusIcon} aria-hidden="true">
                  {STATUS_ICON[row.phase]}
                </span>
                <span className={styles.message}>
                  {text}
                  {row.phase === 'sending' ? (
                    <>
                      {' '}
                      <bdi dir="ltr">{formatPercent(row.percent, locale)}</bdi>
                    </>
                  ) : null}
                  {row.phase === 'scanning' && row.slow ? ` ${t('upload.slow')}` : null}
                </span>
              </div>
            </div>
            <div className={styles.actions}>
              {row.phase === 'failed' && row.retryable ? (
                <Button
                  size="small"
                  icon={<ArrowClockwise16Regular />}
                  aria-label={t('upload.retryLabel', { name })}
                  onClick={() => {
                    controller.retry(row.id);
                  }}
                >
                  {t('common:actions.retry')}
                </Button>
              ) : null}
              <Button
                size="small"
                appearance="subtle"
                aria-label={t(
                  row.phase === 'sending' ? 'upload.cancelLabel' : 'upload.dismissLabel',
                  { name },
                )}
                onClick={() => {
                  controller.remove(row.id);
                }}
              >
                {row.phase === 'sending' ? t('upload.cancel') : t('upload.dismiss')}
              </Button>
            </div>
          </li>
        );
      })}
    </ul>
  );
}


import "/opt/batiflow/app/apps/web/src/features/documents/upload-rows.griffel.css";